import { useState, type FormEvent } from 'react'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import { EMAIL_PATTERN } from '@/lib/email-validation'

interface LoginFormProps {
  onSubmit: (email: string, password: string, rememberMe: boolean) => Promise<void>
  isLoading: boolean
  error?: string | null
  /**
   * When set, the email is pre-filled and read-only — used by the in-place
   * re-auth modal to force re-login as the SAME user (identity lock). Also
   * hides the "remember me / forgot password" row (the forgot-password link
   * would navigate away and destroy the preserved page state).
   */
  lockedEmail?: string
  /** Override the submit-button label (e.g. "Unlock" in the re-auth modal). */
  submitLabel?: string
}

interface FormErrors {
  email?: string
  password?: string
}

/**
 * LoginForm - Login form component per Common-Screens.md Section 1.1
 * Features:
 * - Email input with autofocus
 * - Password input with show/hide toggle
 * - Remember me checkbox
 * - Forgot password link
 * - Client-side validation
 * - Loading state
 * - Inline error messages
 */
export function LoginForm({ onSubmit, isLoading, error, lockedEmail, submitLabel }: LoginFormProps) {
  const [email, setEmail] = useState(lockedEmail ?? '')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  // Validate email format
  const validateEmail = (value: string): string | undefined => {
    if (!value) {
      return 'Email is required'
    }
    const emailRegex = EMAIL_PATTERN
    if (!emailRegex.test(value)) {
      return 'Please enter a valid email address'
    }
    return undefined
  }

  // Validate password
  const validatePassword = (value: string): string | undefined => {
    if (!value) {
      return 'Password is required'
    }
    if (value.length < 8) {
      return 'Password must be at least 8 characters'
    }
    return undefined
  }

  // Validate all fields
  const validateForm = (): boolean => {
    const emailError = lockedEmail ? undefined : validateEmail(email)
    const passwordError = validatePassword(password)

    setErrors({
      email: emailError,
      password: passwordError,
    })

    // Mark all as touched
    setTouched({ email: true, password: true })

    return !emailError && !passwordError
  }

  // Handle field blur for inline validation
  const handleBlur = (field: 'email' | 'password') => {
    setTouched((prev) => ({ ...prev, [field]: true }))

    if (field === 'email') {
      setErrors((prev) => ({ ...prev, email: validateEmail(email) }))
    } else {
      setErrors((prev) => ({ ...prev, password: validatePassword(password) }))
    }
  }

  // Handle form submission
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    await onSubmit(lockedEmail ?? email, password, rememberMe)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Server error message */}
      {error && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Email field */}
      <div className="group space-y-1.5">
        <Label
          htmlFor="email"
          className="transition-colors duration-200 group-focus-within:text-primary"
        >
          Email
        </Label>
        <Input
          id="email"
          type="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => handleBlur('email')}
          autoFocus={!lockedEmail}
          readOnly={!!lockedEmail}
          autoComplete="email"
          aria-invalid={touched.email && !!errors.email}
          aria-describedby={errors.email ? 'email-error' : undefined}
          className={cn(
            'transition-all duration-200 focus-visible:ring-2 focus-visible:ring-primary/30',
            touched.email && errors.email && 'border-destructive focus-visible:ring-destructive/20',
            lockedEmail && 'cursor-not-allowed bg-muted/50 text-muted-foreground',
          )}
          disabled={isLoading}
        />
        {touched.email && errors.email && (
          <p id="email-error" className="text-xs text-destructive animate-in fade-in slide-in-from-top-1 duration-200">
            {errors.email}
          </p>
        )}
      </div>

      {/* Password field */}
      <div className="group space-y-1.5">
        <Label
          htmlFor="password"
          className="transition-colors duration-200 group-focus-within:text-primary"
        >
          Password
        </Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => handleBlur('password')}
            autoFocus={!!lockedEmail}
            autoComplete="current-password"
            aria-invalid={touched.password && !!errors.password}
            aria-describedby={errors.password ? 'password-error' : undefined}
            className={cn(
              'pr-10 transition-all duration-200 focus-visible:ring-2 focus-visible:ring-primary/30',
              touched.password && errors.password && 'border-destructive focus-visible:ring-destructive/20',
            )}
            disabled={isLoading}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors duration-200 hover:text-primary"
            tabIndex={-1}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        </div>
        {touched.password && errors.password && (
          <p id="password-error" className="text-xs text-destructive animate-in fade-in slide-in-from-top-1 duration-200">
            {errors.password}
          </p>
        )}
      </div>

      {/* Remember me and Forgot password — hidden in re-auth mode (the
          forgot-password link would navigate away and lose the preserved page). */}
      {!lockedEmail && (
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="remember"
              checked={rememberMe}
              onCheckedChange={(checked) => setRememberMe(checked === true)}
              disabled={isLoading}
            />
            <Label
              htmlFor="remember"
              className="text-sm font-normal text-muted-foreground cursor-pointer"
            >
              Remember me
            </Label>
          </div>
          <a
            href="/forgot-password"
            className="text-sm text-primary hover:text-primary/80 hover:underline"
            tabIndex={isLoading ? -1 : 0}
          >
            Forgot password?
          </a>
        </div>
      )}

      {/* Submit button — brand-book hover + one-time gold glint on mount */}
      <Button
        type="submit"
        className={cn(
          'btn-glint w-full font-display font-semibold tracking-wide',
          'transition-all duration-200 ease-out',
          'hover:bg-primary-hover hover:-translate-y-px hover:shadow-lg hover:shadow-primary/40',
          'active:translate-y-0 active:shadow-md',
        )}
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Signing in...
          </>
        ) : (
          submitLabel ?? 'Sign In'
        )}
      </Button>
    </form>
  )
}
